import { useCallback, useEffect, useRef, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { DateRange } from '@mui/lab/DateRangePicker'
import { Box, Divider } from '@mui/material'
import {
  IDateFilterVar,
  graphDateRangeVar,
  dateFilterVar,
  dateRangeDropDownQueryParamsVar
} from 'context'
import { format } from 'date-fns'
import { isNumber } from 'lodash-es'
import { useSearchParams } from 'react-router'
import { YYYY_MM_DD } from 'utility/timeFormats'
import { DateRangeQuickFilters } from '../DateRangeQuickFilters'
import { DateQuickFilters, getFormattedCustomRange, parseDateParam } from '../helper'
import { useQuickFilterOptions } from '../hooks'
import { IDateRangeAPI, IDateRangeDropDownProps } from '../types'
import { Arrow, LEFT, RIGHT } from './Arrow'
import { Calendar } from './Calendar'
import { Popover } from './Popover'

export const GlobalDateRange = ({
  initialQuickFilter,
  quickFiltersList
}: IDateRangeDropDownProps) => {
  // this variable contains today's date on the first render
  // when the user change the month on calendar, it gonna have the first day of the visible month
  const [dayInTheVisibleMonth, setDayInTheVisibleMonth] = useState(new Date())
  const [dateRange, setDateRange] = useState<DateRange<Date>>([null, null])
  const [selectedQuickFilter, setSelectedQuickFilter] =
    useState<DateQuickFilters>(initialQuickFilter)
  const [customQuickFilter, setCustomQuickFilter] = useState('')
  const dateRangePopoverRef = useRef<IDateRangeAPI | undefined>(undefined)
  const { startDate: graphStartDate, endDate: graphEndDate } = useReactiveVar(graphDateRangeVar)
  const [searchParams, setSearchParams] = useSearchParams()

  const quickFilterOptions = useQuickFilterOptions(quickFiltersList, customQuickFilter)

  const buildQueryParamsUrl = useCallback(
    (dateFilter: { startDate: string; endDate: string }, selectedQuickFilter: number) => {
      const params = {
        ...dateFilter,
        quickFilter: selectedQuickFilter.toString()
      }

      return new URLSearchParams(params).toString()
    },
    []
  )

  useEffect(() => {
    let startDate, endDate
    const startDateParam = searchParams.get('startDate')
    const endDateParam = searchParams.get('endDate')
    let selectedQuickFilterParam = parseInt(
      searchParams.get('quickFilter') || initialQuickFilter.toString()
    )
    if (!quickFiltersList.includes(selectedQuickFilterParam)) {
      selectedQuickFilterParam = DateQuickFilters.DEFINED
    }

    if (startDateParam && endDateParam) {
      startDate = parseDateParam(startDateParam || '')
      endDate = parseDateParam(endDateParam || '')

      setDayInTheVisibleMonth(startDate)
      setSelectedQuickFilter(selectedQuickFilterParam)
      setDateRange([startDate, endDate])
      if (selectedQuickFilterParam === DateQuickFilters.DEFINED) {
        setCustomQuickFilter(getFormattedCustomRange([startDate, endDate]) || '')
      }
    } else {
      const today = new Date()
      startDate = today
      endDate = today
      setDayInTheVisibleMonth(today)
      setSelectedQuickFilter(initialQuickFilter)
      setDateRange([null, null])
    }

    dateRangeDropDownQueryParamsVar(
      buildQueryParamsUrl(
        {
          startDate: format(startDate, YYYY_MM_DD),
          endDate: format(endDate, YYYY_MM_DD)
        },
        selectedQuickFilterParam
      )
    )

    const quickFilter = isNumber(selectedQuickFilterParam)
      ? { quickFilter: Number(selectedQuickFilterParam) }
      : {}

    dateFilterVar({
      startDate: format(startDate, YYYY_MM_DD),
      endDate: format(endDate, YYYY_MM_DD),
      ...quickFilter
    })
  }, [buildQueryParamsUrl, initialQuickFilter, searchParams, selectedQuickFilter, quickFiltersList])

  const closePopover = () => {
    dateRangePopoverRef.current?.handlePopoverClose()
  }

  const changeDate = (selectedQuickFilter: DateQuickFilters, dateFilter: IDateFilterVar) => {
    if (graphStartDate || graphEndDate) graphDateRangeVar({ startDate: null, endDate: null })

    const url = buildQueryParamsUrl(dateFilter, selectedQuickFilter)
    dateRangeDropDownQueryParamsVar(url)
    setSearchParams(url)
  }

  return (
    <>
      <Arrow
        direction={LEFT}
        selectedQuickFilter={selectedQuickFilter}
        dateRange={dateRange}
        changeDate={changeDate}
      />
      <Popover label={quickFilterOptions[selectedQuickFilter]?.label} ref={dateRangePopoverRef}>
        <Box display="flex">
          <Box width={230} paddingTop={2.5} paddingLeft={2.5}>
            <DateRangeQuickFilters
              closePopover={closePopover}
              quickFilterOptions={quickFilterOptions}
              setSelectedQuickFilter={setSelectedQuickFilter}
              customQuickFilter={customQuickFilter}
              dateRange={dateRange}
              changeDate={changeDate}
            />
          </Box>
          <Divider orientation="vertical" sx={{ height: 360 }} />
          <Box>
            <Calendar
              dateRange={dateRange}
              changeDate={changeDate}
              dayInTheVisibleMonth={dayInTheVisibleMonth}
              setDayInTheVisibleMonth={setDayInTheVisibleMonth}
            />
          </Box>
        </Box>
      </Popover>

      <Arrow
        direction={RIGHT}
        dateRange={dateRange}
        selectedQuickFilter={selectedQuickFilter}
        changeDate={changeDate}
      />
    </>
  )
}
